import React, { useMemo, useState } from 'react';
import { Field, useField } from 'react-final-form';
import {
  MenuItem,
  Autocomplete,
  ListSubheader,
  Typography,
  useTheme,
} from '@mui/material';
import Input from './Input';
import { useTranslation } from 'react-i18next';
import { getTimeZones } from '@vvo/tzdb';
import i18n from 'i18n';

// Helper Functions
const timezones = getTimeZones().sort((a, b) =>
  a.continentName.localeCompare(b.continentName)
);
const userTimezoneCode = Intl.DateTimeFormat().resolvedOptions().timeZone;

const filterOptions = (options, { inputValue }) => {
  const normalizedQuery = inputValue.trim().toLowerCase();
  return options.filter(
    ({ name, mainCities, rawOffsetInMinutes, countryName }) =>
      [name, countryName, ...mainCities].some((text) =>
        i18n.t(text).toLowerCase().includes(normalizedQuery)
      ) ||
      (normalizedQuery.startsWith('+') || normalizedQuery.startsWith('-')
        ? parseInt(normalizedQuery, 10) === rawOffsetInMinutes / 60
        : false)
  );
};

// Sub-components
const GroupHeader = ({ group }) => {
  const theme = useTheme();
  return (
    <ListSubheader
      style={{
        position: 'sticky',
        top: -8,
        fontWeight: 'bold',
        color: theme.palette.colors.black100,
        width: '100%',
      }}
    >
      {group}
    </ListSubheader>
  );
};

const Option = ({ input, option, state }) => {
  const theme = useTheme();
  return (
    <MenuItem
      {...state}
      onClick={() => input.onChange(option)}
      key={option.name}
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        width: '100%',
        overflowX: 'hidden',
      }}
    >
      <Typography
        style={{
          wordBreak: 'break-word',
          whiteSpace: 'normal',
        }}
      >
        {`${i18n.t(option.countryName)}, ${i18n.t(option.mainCities[0])}`}
      </Typography>
      <Typography
        style={{
          display: 'flex',
          padding: 4,
          borderRadius: 6,
          backgroundColor: theme.palette.colors.gray200,
          fontSize: 12,
        }}
      >
        {`${option.abbreviation} ${option.rawFormat.slice(0, 7)}`}
      </Typography>
    </MenuItem>
  );
};

// Main Component
const FormTimezoneSelector = ({ name }) => {
  const { t } = useTranslation();
  const defaultValue = useField(name)?.input?.value;

  const defaultTimezone = useMemo(
    () => {
      let timezone = null

      if (defaultValue) {
        timezone = timezones.find((tz) =>
          tz.group.includes(defaultValue)
        )
      }

      if (!timezone && userTimezoneCode) {
        timezone = timezones.find((tz) =>
          tz.group.includes(userTimezoneCode)
        )
      }

      if (!timezone) {
        timezone = timezones.find((tz) =>
          tz.group.includes("Europe/Paris")
        )
      }
    
      return timezone
    },
    [defaultValue]
  );

  return (
    <Field name={name} initialValue={defaultTimezone}>
      {({ input, meta }) => (
        <Autocomplete
          style={{ width: '100%' }}
          {...input}
          filterOptions={filterOptions}
          options={timezones}
          groupBy={(option) => option.continentName}
          isOptionEqualToValue={(option, value) => option.name === value.name}
          getOptionLabel={(option) =>
            option
              ? `${t(option?.countryName)}, ${t(option?.mainCities?.[0])} (${
                  option?.abbreviation
                })`
              : ''
          }
          renderGroup={(params) => (
            <li style={{ width: '100%' }}>
              <GroupHeader group={params.group} />
              {params.children}
            </li>
          )}
          renderOption={(props, option, state) => (
            <Option {...props} input={input} option={option} state={state} />
          )}
          renderInput={(params) => (
            <Input
              {...params}
              placeholder={t('Select a timezone')}
              error={meta.touched && meta.error}
              helperText={meta.touched && t(meta.error)}
            />
          )}
        />
      )}
    </Field>
  );
};

export default FormTimezoneSelector;
